import { AttributeDefRow, AttributeDefRowView, AttributeTypeValue, ProfileAttribute, profileAttributeSchema } from "@bonusmap/api-types";
import { ProfileBaseComponent } from "./profile-base-component.js";
import { ProfileAttributeItem } from "./profile-attribute-item.js";
import { ProfileAttributeValue } from "./profile-attribute-value.js";
import { isNumber } from "@bonusmap/shared_ui/library";
import { IProfileAttributes } from "./IProfileAttributes.js";

// [claude] Web component for attribute matching UI - combines item selection with value input
export class ProfileAttributeComponent extends ProfileBaseComponent {
    static webComponentName: string = "profile-attribute";
    static observedAttributes = ["attribute-id"]; // Osserva l'attributo "attribute-id"
    protected _templateId: string = "profile-attribute";
    // [claude] Child components for attribute selection and value configuration
    private attributeItem!: ProfileAttributeItem;
    private attributeValue!: ProfileAttributeValue
    private _componentId!: string;
    private _attributeId: number = 0;
    private _profileValue: number | Date | boolean | null = null;
    public get parentListContainer(): IProfileAttributes | null {
        return this.closest('profile-attributes');
    }
    public get value(): ProfileAttribute {
        return {
            attribute_id: this._attributeId,
            value: this._profileValue
        };
    }
    public get valid(): boolean {
        const parsed = profileAttributeSchema.safeParse(this.value);
        return parsed.success;
    }
    private evalStateClass(): void {
        const stateClass = this.valid ? 'is-valid' : 'is-invalid';
        // Applica la classe di stato al componente 
        this.classList.remove('is-valid', 'is-invalid');
        this.classList.add(stateClass);

    }
    public get attributeId(): number {
        return this._attributeId;
    }

    public set attributeId(id: number) {
        this._attributeId = id;
        this.dispatch('value-changed', { origin: this });
    }

    public get profileValue(): number | Date | boolean | null {
        return this._profileValue;
    }

    public set profileValue(val: number | Date | boolean | null) {
        this._profileValue = val;
        this.dispatch('value-changed', { origin: this });
    }

    constructor() {
        super();
    }
    protected onConnected(): void {
        this.addEventListener('value-changed', (event: any) => {
            this.evalStateClass();
            if (event.target !== this) {
                this.dispatch('value-changed', { origin: this });
            }
        });
    }
    async getAttributeById(attributeId: number): Promise<AttributeDefRowView | null> {
        if (!isNumber(attributeId) || attributeId <= 0) return null;
        const result = await window.api.manager.personalAttributes.getByIdView(attributeId);
        if (result.success && result.data && this.isAttributeValid(result.data)) {
            return result.data;
        }
        else {
            this.dispatch('attribute-not-found', { attributeId: attributeId, origin: this });
            return null;
        }
    }
    // [claude] Lazy-loaded unique identifier for component instance
    public get componentId(): string {
        if (!this._componentId) {
            this._componentId = `${this.createRandomId()}`;
        }
        return this._componentId;
    }

    protected buildRenderingData(): Record<string, any> {
        let profileValueAttr = this.getAttribute('value') || "";

        const data = {
            parentListContainer: this.parentListContainer,
            componentId: this.componentId,
            value: this.value,
            profileValue: this.profileValue || profileValueAttr || null,
            personalAttribute: this.personalAttribute,
            readOnly: this.readOnly
        };
        return data;
    }
    private _personalAttribute!: AttributeDefRow;
    public get personalAttribute(): AttributeDefRow {
        return this._personalAttribute;
    }
    private onSettingPersonalAttribute(attribute: AttributeDefRow | null): void {
        if (this.isAttributeValid(attribute)) {
            const type_value: AttributeTypeValue = attribute.type_value;
            this.attributeId = attribute.id;
            this.attributeValue!.type_value = type_value;
        }
        else {
            this.attributeId = 0;
            this.profileValue = null;
            this.attributeValue!.type_value = null;

        }
    }
    public set personalAttribute(attribute: AttributeDefRow) {
        this.onSettingPersonalAttribute(attribute);
        this._personalAttribute = attribute;
    }
    private removeAttributeButton!: HTMLButtonElement;
    // [claude] Initialize child component references after DOM rendering
    protected afterRender(): void {
        this.attributeItem = this.querySelector("profile-attribute-item") as ProfileAttributeItem;
        this.attributeValue = this.querySelector("profile-attribute-value") as ProfileAttributeValue;
        this.attributeItem.addEventListener('attribute-changed', (event: any) => {
            const attribute = event.detail.attribute as AttributeDefRow | null;
            this.personalAttribute = attribute;

            this.attributeValue?.resetDataValue();

            this._profileValue = null;

        });
        this.attributeValue.addEventListener('value-changed', (event: any) => {
            this.profileValue = event.detail.value;
        });
        this.onSettingPersonalAttribute(this.personalAttribute);
        this.evalStateClass();
        this.removeAttributeButton = this.roleElementSelector('remove-attribute') as HTMLButtonElement;
        this.removeAttributeButton?.addEventListener('click',
            (event: Event) => {
                event.preventDefault();
                this.dispatch('remove-attribute', { origin: this });
            });

    }

    private renderedOnce: boolean = false;
    protected render(): void {
        if (this.renderedOnce) {
            super.render();
            return;
        }
        this.renderedOnce = true;
        const attributeIdAttr = this.getAttribute('attribute-id');
        if (isNumber(attributeIdAttr) && Number(attributeIdAttr) > 0) {
            this.innerHTML = ''; // Pulisce il contenuto prima di caricare
            const attributeId = Number(attributeIdAttr);
            this.getAttributeById(attributeId)
                .then(attribute => {
                    console.log("getAttributeById resolved", attribute);
                    if (attribute) {
                        this._personalAttribute = attribute;
                    }
                    super.render();
                })
                .catch(err => {
                    super.render();
                });
        }
        else {
            super.render();
        }
    }
    protected onAttributeChange(name: string, _oldValue: string, _newValue: string): void {
        if (name === 'attribute-id') {
            this.renderedOnce = false;
            this.render();
        }
    }

    // [claude] Register this component and its dependencies in DOM custom elements registry
    static registerElement(): void {
        ProfileAttributeItem.registerElement();
        ProfileAttributeValue.registerElement();
        super.registerElement();
    }
}